// Maps request host → whitelabel operator slug (strapi-gbs + main Strapi; mirrors stationDomains.ts).

interface StrapiListResponse {
  data: Record<string, any>[];
  meta?: unknown;
}

export interface OperatorDomainEntry {
  operator: string; // v3-web-resources.operator slug
  domains: string[]; // normalized hosts (no www, no port)
}

const STRAPI_GBS_ENDPOINT = "/api/whitelabels?populate=*";
const STRAPI_MAIN_ENDPOINT = "/api/white-labels?pagination%5BpageSize%5D=100";
const CACHE_TTL_MS = 5 * 60 * 1000;

// Server → client domain list for sync helpers (no extra fetch).
export const OPERATOR_DOMAINS_WINDOW_KEY = "__OPERATOR_DOMAINS__";

let cachedData: OperatorDomainEntry[] | null = null;
let cacheTimestamp = 0;

const SLUG_KEYS = [
  "operator",
  "slug",
  "subdomain",
  "subdominio",
  "cms_username",
  "pathname",
  "name",
] as const;
const DOMAIN_ARRAY_KEYS = ["domains", "custom_domains"] as const;
const DOMAIN_STRING_KEYS = [
  "domain",
  "custom_domain",
  "url",
  "operator_website",
  "link_home",
] as const;

function normalizeHost(value: string): string {
  if (!value) return "";
  let host = value.trim().toLowerCase();
  try {
    if (host.includes("://")) host = new URL(host).host;
  } catch {
    // not a full URL; treat as bare host
  }
  host = host.split(":")[0];
  host = host.replace(/^www\./, "");
  return host;
}

function pickFirstString(
  obj: Record<string, any>,
  keys: readonly string[]
): string | null {
  for (const key of keys) {
    const v = obj?.[key];
    if (typeof v === "string" && v.trim()) return v.trim();
  }
  return null;
}

function collectDomains(obj: Record<string, any>): string[] {
  const out = new Set<string>();

  for (const key of DOMAIN_ARRAY_KEYS) {
    const v = obj?.[key];
    if (Array.isArray(v)) {
      for (const item of v) {
        if (typeof item === "string" && item.trim())
          out.add(normalizeHost(item));
      }
    } else if (typeof v === "string" && v.trim()) {
      // some Strapi setups store JSON arrays as a string
      const parsed = tryParseJsonArray(v);
      if (parsed) parsed.forEach((d) => out.add(normalizeHost(d)));
      else out.add(normalizeHost(v));
    }
  }

  for (const key of DOMAIN_STRING_KEYS) {
    const v = obj?.[key];
    if (typeof v === "string" && v.trim()) out.add(normalizeHost(v));
  }

  return Array.from(out).filter(Boolean);
}

function tryParseJsonArray(raw: string): string[] | null {
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.every((x) => typeof x === "string")) {
      return parsed;
    }
  } catch {
    /* not JSON */
  }
  return null;
}

function parseStrapiEntries(json: StrapiListResponse): OperatorDomainEntry[] {
  if (!Array.isArray(json?.data)) return [];

  const entries: OperatorDomainEntry[] = [];
  for (const entry of json.data) {
    // Strapi v4 nests under `attributes`; v5 puts fields flat on the entry
    const attrs = entry?.attributes ?? entry ?? {};
    const slug = pickFirstString(attrs, SLUG_KEYS);
    if (!slug) continue;

    entries.push({
      operator: slug.toLowerCase(),
      domains: collectDomains(attrs),
    });
  }
  return entries;
}

async function fetchFromStrapi(
  url: string,
  label: string
): Promise<OperatorDomainEntry[]> {
  try {
    const response = await fetch(url, {
      cache: "no-store",
      next: { revalidate: 0 },
    });
    if (!response.ok) {
      console.error(`[operatorDomains] ${label} returned ${response.status}`);
      return [];
    }
    return parseStrapiEntries(await response.json());
  } catch (error) {
    console.error(`[operatorDomains] failed to fetch ${label}:`, error);
    return [];
  }
}

export async function fetchOperatorDomains(): Promise<OperatorDomainEntry[]> {
  // Strapi (content.embarca.ai) desativado — sem mapeamento de domínios de operador.
  return [];
}

export async function getCachedOperatorDomains(): Promise<
  OperatorDomainEntry[]
> {
  const now = Date.now();
  if (cachedData !== null && now - cacheTimestamp < CACHE_TTL_MS) {
    return cachedData;
  }

  const fresh = await fetchOperatorDomains();
  cachedData = fresh;
  cacheTimestamp = now;
  return fresh;
}

// Async host → operator slug; null → use legacy host.split('.')[0].
export async function resolveOperatorFromHost(
  host: string
): Promise<string | null> {
  if (!host) return null;
  const normalized = normalizeHost(host);
  if (!normalized) return null;

  const entries = await getCachedOperatorDomains();
  return matchOperator(entries, normalized);
}

// Sync domain list: server cache, else window seed from layout.
export function getOperatorDomainsSync(): OperatorDomainEntry[] {
  if (cachedData && cachedData.length > 0) return cachedData;

  if (typeof window !== "undefined") {
    const seeded = (window as any)[OPERATOR_DOMAINS_WINDOW_KEY];
    if (Array.isArray(seeded)) return seeded as OperatorDomainEntry[];
  }

  return [];
}

// Sync host → operator slug (getOperatorName, getPlatform, etc.).
export function resolveOperatorFromHostSync(host: string): string | null {
  if (!host) return null;
  const normalized = normalizeHost(host);
  if (!normalized) return null;

  const entries = getOperatorDomainsSync();
  return matchOperator(entries, normalized);
}

// princesadoscampos.localhost → slug; bare localhost → null.
export function getOperatorSlugFromLocalhostHost(host: string): string | null {
  const hostname = host
    .split(":")[0]
    .toLowerCase()
    .replace(/^www\./, "");
  if (!hostname.endsWith(".localhost")) return null;

  const slug = hostname.slice(0, -".localhost".length);
  if (!slug || slug === "localhost" || slug === "passagens") return null;

  return slug;
}

export function isOperatorLocalhostHost(host: string): boolean {
  return getOperatorSlugFromLocalhostHost(host) !== null;
}

// Custom-domain / operator.localhost hosts use Next proxy for per-operator X-API-TOKEN.
export function shouldRouteApiThroughProxy(host: string): boolean {
  const hostname = host.split(":")[0].toLowerCase();

  if (hostname === "localhost" || hostname === "127.0.0.1") return false;
  if (hostname.endsWith(".embarca.ai") || hostname === "embarca.ai")
    return false;
  if (hostname.endsWith(".herokuapp.com")) return false;
  if (
    hostname.endsWith(".embarca-staging.com.br") ||
    hostname === "embarca-staging.com.br"
  ) {
    return false;
  }

  if (isOperatorLocalhostHost(host)) return true;

  return resolveOperatorFromHostSync(host) !== null;
}

// Client: current host → operator slug (fallback: host.split('.')[0]).
export function getOperatorFromCurrentHost(): string | null {
  if (typeof window === "undefined") return null;
  return (
    resolveOperatorFromHostSync(window.location.host) ??
    getOperatorSlugFromLocalhostHost(window.location.host)
  );
}

function matchOperator(
  entries: OperatorDomainEntry[],
  normalizedHost: string
): string | null {
  // 1. Explicit domain from Strapi (domain, operator_website, etc.)
  for (const entry of entries) {
    if (
      entry.domains.length > 0 &&
      entry.domains.some((d) => d === normalizedHost)
    ) {
      return entry.operator;
    }
  }

  // 2. Slug-prefix on custom TLD (not .embarca.ai)
  if (!normalizedHost.endsWith(".embarca.ai")) {
    for (const entry of entries) {
      if (normalizedHost.startsWith(`${entry.operator}.`)) {
        return entry.operator;
      }
    }
  }

  // 3. operator.localhost (local dev)
  const localhostSlug = getOperatorSlugFromLocalhostHost(normalizedHost);
  if (localhostSlug) return localhostSlug;

  return null;
}

export function clearOperatorDomainCache(): void {
  cachedData = null;
  cacheTimestamp = 0;
}
